"use client";

import React, { useRef, useState } from "react";
import {
  markerPopupOffset,
  RLayer,
  RMap,
  RPopup,
  RSource,
} from "maplibre-react-components";
import type {
  CircleLayerSpecification,
  LngLatLike,
  Map as MapLibre,
  MapLibreEvent,
} from "maplibre-gl";
import { SiteMapMarker } from "@/components";
import { useAtomValue } from "jotai";
import { mapTileAtom } from "@/store";
import { SiteType } from "@/types";
import { useTailwind } from "@oreme/ui";
import { point } from "@turf/turf";

interface SiteMapProps {
  site: SiteType;
}

function SiteMap({ site }: Readonly<SiteMapProps>) {
  const data = point([site.longitude, site.latitude]);
  const mapRef = useRef<MapLibre>(null);
  const tailwind = useTailwind();
  const tile = useAtomValue(mapTileAtom);
  const [isLoaded, setIsLoaded] = useState(false);
  const center: LngLatLike = [site.longitude, site.latitude];
  const zoom = 10;

  const paint: CircleLayerSpecification["paint"] = React.useMemo(
    () => ({
      "circle-color": tailwind.theme.colors.orange[500],
      "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 1, 2, 12, 2],
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 1, 2, 12, 5],
      "circle-stroke-color": tailwind.theme.colors.orange[700],
    }),
    [tailwind],
  );

  const handleLoad = (e: MapLibreEvent) => {
    const map = e.target;
    map.setProjection({
      type: "mercator",
    });
    setIsLoaded(true);
  };

  return (
    <RMap
      className={`${isLoaded ? "animate__animated animate__fadeIn animate__faster" : "hidden"} bg-accent`}
      id="site-map"
      initialCenter={center}
      initialZoom={zoom}
      mapStyle={tile.url}
      ref={mapRef}
      onLoad={handleLoad}
      initialAttributionControl={false}
    >
      <RPopup
        latitude={site.latitude}
        longitude={site.longitude}
        offset={markerPopupOffset}
      >
        <SiteMapMarker site={site} />
      </RPopup>
      <RSource
        key="site-source"
        id="site-source"
        generateId={true}
        type="geojson"
        data={data}
        promoteId="site_id"
      />
      <RLayer
        key="site-layer"
        id="site-layer"
        source="site-source"
        type="circle"
        paint={paint}
      />
    </RMap>
  );
}

export { SiteMap };
